"use client";

import { useRouter } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "@/components/icons/ArrowRight";

import {
  Button as ButtonComponent,
  type ButtonVariant,
  type ButtonSize,
} from "@/components/ui/button";
import { useAuthModal } from "@/components/auth/AuthModalContext";
import { setReturnIntent } from "@/lib/auth/return-intent";
import type { ButtonStoryblok } from "@/component-types-sb";

const variantMap: Record<string, ButtonVariant> = {
  primary: "black",
  secondary: "white",
  sun: "sun",
  black: "black",
  white: "white",
  "mylife-green": "mylife",
  dexcom: "dexcom",
  minimed: "minimed",
};

const sizeMap: Record<string, ButtonSize> = {
  sm: "sm",
  lg: "lg",
  default: "sm",
};

const AuthModalButton = ({ blok }: { blok: ButtonStoryblok }) => {
  const { setOpen } = useAuthModal();
  const { isSignedIn } = useUser();
  const router = useRouter();
  const mappedVariant: ButtonVariant = variantMap[blok.variant || ""] ?? "black";
  const mappedSize: ButtonSize = sizeMap[blok.size || ""] ?? "sm";

  const handleClick = () => {
    if (isSignedIn) {
      router.push("/dashboard");
      return;
    }
    if (window.innerWidth < 1280) {
      setReturnIntent(window.location.pathname, "auth_modal_button");
      router.push(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
    } else {
      setOpen(true);
    }
  };

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(); }}
      className="cursor-pointer"
      data-cta-location="content_button"
      data-cta-label={blok.label}
      {...storyblokEditable(blok)}
    >
      <ButtonComponent
        variant={mappedVariant}
        size={mappedSize}
      >
        {blok.arrow === "left" && (
          <ArrowRight size={mappedSize === "lg" ? "lg" : "sm"} />
        )}
        {blok.label}
        {blok.arrow === "right" && (
          <ArrowRight size={mappedSize === "lg" ? "lg" : "sm"} />
        )}
      </ButtonComponent>
    </div>
  );
};

export default AuthModalButton;
